/**
 * De modeltegels van de Chats-pagina: één tegel per SLUG, niet per persona.
 *
 * 02-10-2026 (Jordy): verhuisd uit pages/Chats.tsx, zodat de Pro-stand (de
 * tegelrij bovenaan) en de Easy-stand (de modellijst rechts) precies dezelfde
 * modellen in dezelfde volgorde tonen. De berekening zelf is ongewijzigd.
 *
 * Een persona kan meerdere slugs hebben (fc_ én f2f_, plus soms een
 * nas_-sjabloon met 0 chats) en de oude pagina toont die allemaal apart.
 * Volgorde in oud: modellen met openstaande chats eerst, daarna alfabetisch.
 */
import type { Persona } from '../pages/Models'

export interface ModelTegel {
  key: string
  id: number
  name: string
  slug: string
  avatar: string | null
  platform: string
  chats: number
  unread: number
}

export function modelTegels(personas: Persona[] | null | undefined, platform = 'all'): ModelTegel[] {
  const out: ModelTegel[] = []
  for (const p of personas ?? []) {
    const bySlug = p.conversation_count_by_slug ?? null
    const slugs = bySlug
      ? Object.entries(bySlug)
      : [[p.slug, { total: p.conversation_count, unanswered: p.unanswered_count }] as const]
    for (const [slug, v] of slugs) {
      out.push({
        key: `${p.id}-${slug}`,
        id: p.id,
        // Oud toont voor ELKE slug van een persona dezelfde naam: die van
        // de persona zelf. `persona_stage_names_by_slug` geeft afwijkende
        // namen ("Lana Lux", "Ruwetom") die het oude scherm niet gebruikt.
        name: p.stage_name || p.slug || slug,
        slug: String(slug),
        avatar: p.avatar_url,
        // nas_-sjabloonslugs erven het platform van de persona zelf.
        platform: String(slug).startsWith('fc_') ? 'fancentro'
                : String(slug).startsWith('f2f_') ? 'f2f'
                : p.fancentro_model_id ? 'fancentro' : 'f2f',
        chats: Number(v?.total ?? 0),
        unread: Number(v?.unanswered ?? 0),
      })
    }
  }
  return out
    .filter((t) => (platform === 'all' ? true : t.platform === platform))
    .sort((a, b) =>
      (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0) ||
      String(a.name).localeCompare(String(b.name), 'nl'))
}

/**
 * 18-10-2026 (Jordy): de model-cards tonen het aantal NU OPEN chats per model,
 * precies de rijen achter de "open chats"-balk (`bucketPanels(...).open`), en
 * niet meer het totaal aantal gesprekken ooit. Volgorde: meeste open chats
 * links, aflopend; bij gelijk aantal eerst met "new", dan alfabetisch.
 */
export function openPerSlug(rijen: readonly object[] | null | undefined): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rijen ?? []) {
    const slug = String((r as { model_slug?: unknown })?.model_slug ?? '')
    if (slug) m.set(slug, (m.get(slug) ?? 0) + 1)
  }
  return m
}

/**
 * 23-10-2026 (Jordy): ``nieuw`` = per model de OPEN chats die sinds het laatste
 * fanbericht nog niet in het dashboard bekeken zijn. Dan is "open" alles wat wacht
 * en "new" wat nog een eerste blik nodig heeft (dezelfde gefilterde set: geen
 * timewasters, geblokkeerde of beantwoorde chats). Zonder ``nieuw``: zoals voorheen.
 */
export function tegelsOpOpen(tegels: ModelTegel[], open: Map<string, number>,
                             nieuw?: Map<string, number>): (ModelTegel & { open: number })[] {
  return tegels
    .map((t) => ({ ...t, open: open.get(t.slug) ?? 0, unread: nieuw ? (nieuw.get(t.slug) ?? 0) : t.unread }))
    .sort((a, b) =>
      b.open - a.open ||
      (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0) ||
      String(a.name).localeCompare(String(b.name), 'nl'))
}

/** Foto per model-slug (ook de extra slugs van een persona), zoals de
 *  Pro-stand die opbouwt. Voor de rijen in de Easy-stand. */
export function avatarZoeker(personas: Persona[] | null | undefined) {
  const m = new Map<string, string>()
  for (const p of personas ?? []) {
    if (p.slug && p.avatar_url) m.set(String(p.slug), p.avatar_url)
    for (const slug of p.persona_slugs ?? []) {
      if (slug && p.avatar_url) m.set(String(slug), p.avatar_url)
    }
  }
  return (slug: unknown) => m.get(String(slug ?? '')) ?? undefined
}
